import moment from "moment";
import { OverlayTrigger, Tooltip } from "react-bootstrap";

import { useAuth } from "providers/Auth";
import { RoleDescription } from "types/enums";
import { Quote } from "types/quotes";
import { formatPrice } from "utils/formats";

interface ProfessionalQuoteReportCardProps {
  quote: Quote;
}

const isZeroDate = (value?: string | null) =>
  !value || value.startsWith("0001-01-01");

const formatDate = (value?: string | null) =>
  isZeroDate(value) ? null : moment(value).format("DD/MM/YYYY");

const ProfessionalQuoteReportCard = ({
  quote,
}: ProfessionalQuoteReportCardProps) => {
  const { userRole } = useAuth();
  const payment = quote.payment;

  const isLink = !!payment?.linkUrl;
  const paymentDateDisplay = formatDate(payment?.paymentDate);
  const repasseDateDisplay = formatDate(payment?.paymentLiquidationDate);

  return (
    <div className="row-item mb-2 px-3 pt-3">
      <div className="row align-items-center no-gutters">
        {/* Id + Tipo Pagamento */}
        <div className="col col-auto d-flex align-items-center flex-column pb-3 pr-3 gap-1">
          <p className="small opacity-75 mb-0">Id Orçamento</p>
          <b>#{quote.id}</b>
          <span
            className={`badge ${isLink ? "bg-success" : "bg-primary"}`}
            style={{ fontSize: "0.65rem", whiteSpace: "nowrap" }}
          >
            {isLink ? (
              <>
                <i className="uil uil-link" /> Link
              </>
            ) : (
              <>
                <i className="uil uil-store" /> Máquina
              </>
            )}
          </span>
        </div>

        {/* Nome do Prestador */}
        <div className="col col-xxl pb-3 px-1">
          <p className="small opacity-75 mb-0">Nome Prestador</p>
          <OverlayTrigger
            placement="top"
            overlay={<Tooltip>{quote.professional?.name}</Tooltip>}
          >
            <b className="d-block text-md-truncate">
              {quote.professional?.name}
            </b>
          </OverlayTrigger>
        </div>

        {/* Nome do Paciente */}
        <div className="col col-xxl pb-3 px-1">
          <p className="small opacity-75 mb-0">Nome Paciente</p>
          {quote.patient?.name ? (
            <OverlayTrigger
              placement="top"
              overlay={<Tooltip>{quote.patient.name}</Tooltip>}
            >
              <b className="d-block text-md-truncate">{quote.patient.name}</b>
            </OverlayTrigger>
          ) : (
            <span className="opacity-50">—</span>
          )}
        </div>

        <div className="col-12 col-md-8 col-xl-8 col-xxl-6">
          <div className="row">
            {/* Valor Total */}
            <div className="col-12 col-sm-2 col-md-2 pb-3">
              <p className="small opacity-75 mb-0 text-truncate">Valor Total</p>
              <b>{formatPrice(payment?.value)}</b>
            </div>

            {/* Custo CET */}
            <div className="col-12 col-sm-2 col-md-2 pb-3">
              <p className="small opacity-75 mb-0 text-truncate">Custo CET</p>
              <b>{formatPrice(payment?.cetCost)}</b>
            </div>

            {/* Qtd Parcela */}
            <div className="col-12 col-sm-2 col-md-2 pb-3">
              <p className="small opacity-75 mb-0 text-truncate">Parcelas</p>
              <b>{payment?.parcelQtd}x</b>
            </div>

            {/* Data de Pagamento */}
            <div className="col-12 col-sm-2 col-md-2 pb-3">
              <p className="small opacity-75 mb-0 text-truncate">
                Data Pagamento
              </p>
              {paymentDateDisplay ? (
                <b>{paymentDateDisplay}</b>
              ) : (
                <p className="opacity-75 mb-0 text-truncate">---------------</p>
              )}
            </div>

            {/* Valor do Repasse */}
            <div className="col-12 col-sm-2 col-md-2 pb-3">
              <p className="small opacity-75 mb-0 text-truncate">
                Valor Repasse
              </p>
              <b>{formatPrice(payment?.compensationValue)}</b>
            </div>

            {/* Data de Repasse */}
            <div className="col-12 col-sm-2 col-md-2 pb-3">
              <p className="small opacity-75 mb-0 text-truncate">
                Data Repasse
              </p>
              {repasseDateDisplay ? (
                <b>{repasseDateDisplay}</b>
              ) : (
                <OverlayTrigger
                  placement="top"
                  overlay={
                    <Tooltip>
                      Cartão aprovado, repasse ainda não foi efetuado
                    </Tooltip>
                  }
                >
                  <span className="badge bg-warning text-dark">Pendente</span>
                </OverlayTrigger>
              )}
            </div>

            {/* PaymentCode */}
            {userRole === RoleDescription.Admin && (
              <div className="col-12 col-sm-2 col-md-2 pb-3">
                <p className="small opacity-75 mb-0">Pagamento</p>
                {payment?.orderId ? (
                  <OverlayTrigger
                    placement="top"
                    overlay={<Tooltip>{payment.orderId}</Tooltip>}
                  >
                    <b className="d-block text-sm-truncate">
                      #{payment.orderId}
                    </b>
                  </OverlayTrigger>
                ) : (
                  <span className="opacity-75">---------</span>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfessionalQuoteReportCard;
